import { useDeviceTypeClientListingEditor } from '@admin/hooks/useDeviceTypeClientListingEditor';
import styled from '@emotion/styled';
import { TextField } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import SplitButton from '@shared/components/core/button/SplitButton';
import TabButton from '@shared/components/core/button/TabButton';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import Icons from '@shared/components/core/images/Icons';
import { BodySmall, BodyXSmall } from '@shared/components/core/text/Typography';
import { SpacedProgress } from '@shared/components/loading/SpacedLoader';
import ResourceMarkdownEditor from '@shared/components/organization/details/ResourceMarkdownEditor';
import AlertsContainer from '@shared/hooks/state/AlertsContainer';
import EditDeviceTypeContainer from '@shared/hooks/state/EditDeviceTypeContainer';
import useResolveCurrentOrg from '@shared/hooks/useResolveCurrentOrg';
import { DisplayLocales } from '@shared/models/localization/Locales';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const ContentWrapper = styled.div`
    display: flex;
    flex: 1;
    gap: 0.5rem;
`;

const Sidebar = styled.aside`
    display: flex;
    width: fit-content;
    flex-shrink: 0;
    flex-direction: column;
    padding-right: 0.5rem;
    border-right: 1px solid ${(props) => props.theme.color.border};
    background-color: var(--sidebar);
    gap: 1rem;
`;

const SectionLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding-top: 1rem;
    color: var(--muted-foreground);
`;

const NavList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const CompletionButton = styled.button<{ $isActive: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 0.375rem;
    background-color: ${({ $isActive, theme }) => ($isActive ? theme.color.accent + '30' : 'transparent')};
    color: ${(props) => props.theme.color.text};
    transition: background-color 0.2s;
    cursor: pointer;
    border: none;
    text-align: left;

    &:hover {
        background-color: ${({ $isActive, theme }) =>
            $isActive ? theme.color.accent + '30' : theme.color.accent + '15'};
    }
`;

const CompletionLabel = styled.span`
    flex: 1;
    color: ${(props) => props.theme.color.text};
`;

const CompletionPercentage = styled.span<{ $isComplete: boolean }>`
    font-weight: 500;
    color: ${({ $isComplete, theme }) => ($isComplete ? theme.color.success : theme.color.textBody)};
`;

const MainContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 1rem 0;
`;

const EditorSection = styled.div`
    flex: 1;
    overflow: hidden;
`;

const EditorContainer = styled.div`
    height: 100%;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background-color: var(--card);
    overflow: hidden;
`;

const MetadataForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    overflow-y: auto;
    height: 100%;
`;

const StyledTextField = styled(TextField)<{ error?: boolean }>`
    & .MuiOutlinedInput-root {
        &.Mui-error .MuiOutlinedInput-notchedOutline {
            border-color: ${(props) => props.theme.color.danger};
        }
        &.Mui-error:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${(props) => props.theme.color.danger};
        }
        &.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${(props) => props.theme.color.danger};
        }
    }
    & .MuiFormHelperText-root.Mui-error {
        color: ${(props) => props.theme.color.danger};
    }
`;

const DocumentDescription = styled.div`
    color: ${(props) => props.theme.color.textBody};
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: ${(props) => props.theme.color.cardHover};
    border-radius: 0.375rem;
    border: 1px solid ${(props) => props.theme.color.border};
`;

const DOCUMENT_TYPES = [
    { id: 'metadata', icon: 'fileMd' },
    { id: 'client-listing', icon: 'fileMd' },
    { id: 'troubleshooting', icon: 'fileMd' },
    { id: 'onboarding', icon: 'fileMd' },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]['id'];
type LocaleCode = DisplayLocales;

interface ProjectDeviceTypeClientListingProps {
    hideSaveButton?: boolean; // Option to hide the save button for usage in different contexts
}

export default function ProjectDeviceTypeClientListing({
    hideSaveButton = false,
}: ProjectDeviceTypeClientListingProps) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { deviceId } = useParams();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const { loadingListing, listingHasChanges } = EditDeviceTypeContainer.useContainer();

    const {
        activeDocument,
        setActiveDocument,
        activeLocale,
        setActiveLocale,
        isSaving,
        LOCALES,
        currentContent,
        currentListing,
        isValidUrl,
        getLocaleCompletion,
        handleContentChange,
        handleMetadataFieldChange,
        saveListings,
        isDocumentComplete,
        isDocumentEmpty,
    } = useDeviceTypeClientListingEditor(organizationId, deviceId);

    const handleSave = async () => {
        await saveListings(showSuccess, showError);
    };

    if (loadingListing) {
        return <SpacedProgress />;
    }

    return (
        <>
            {/* Header */}
            <SubSectionRow title={'Cephable In-App Documentation Editor'} $sticky={true}>
                {!hideSaveButton && (
                    <Button onClick={handleSave} disabled={!listingHasChanges || isSaving}>
                        {isSaving ? t('common:saving') : t('common:save')}
                    </Button>
                )}
            </SubSectionRow>

            <ContentWrapper>
                {/* Sidebar */}
                <Sidebar>
                    <div>
                        <SectionLabel>
                            <Icons name="globe" ariaLabel={t('common:localization.titleShort')} />
                            {t('common:localization.titleShort')}
                        </SectionLabel>
                        <SplitButton
                            options={LOCALES.map(
                                (locale: { code: DisplayLocales; label: string; flag: string }) => locale.code
                            )}
                            selectedIndex={LOCALES.findIndex(
                                (l: { code: DisplayLocales; label: string; flag: string }) => l.code === activeLocale
                            )}
                            renderOptionLabel={(option) => {
                                const locale = LOCALES.find(
                                    (l: { code: DisplayLocales; label: string; flag: string }) => l.code === option
                                );
                                return (
                                    <span className="flex items-center gap-2">
                                        <span>{locale?.flag}</span>
                                        <span>{locale?.label}</span>
                                    </span>
                                );
                            }}
                            groupLabel={t('common:localization.select')}
                            onSelect={(option) => setActiveLocale(option as LocaleCode)}
                        />
                    </div>
                    {/* Document Navigation */}

                    <SectionLabel id="documents-label">{t('common:organization.documents')}</SectionLabel>
                    <NavList aria-labelledby="documents-label">
                        {DOCUMENT_TYPES.map((doc) => {
                            const isComplete = isDocumentComplete(doc.id);
                            const isEmpty = isDocumentEmpty(doc.id);
                            return (
                                <TabButton
                                    key={doc.id}
                                    isActive={activeDocument === doc.id}
                                    iconName={isComplete ? 'check' : 'warningTriangle'}
                                    statusAlert={isComplete ? 'success' : 'warning'}
                                    onClick={() => setActiveDocument(doc.id)}>
                                    {t(`common:organization.${doc.id}.title`)}
                                </TabButton>
                            );
                        })}
                    </NavList>

                    {/* Completion Status */}

                    <SectionLabel id="completion-status-label">
                        {t('common:organization.completionStatus.title')}
                    </SectionLabel>
                    <NavList aria-labelledby="completion-status-label">
                        {LOCALES.map((locale: { code: DisplayLocales; label: string; flag: string }) => {
                            const percentage = getLocaleCompletion(locale.code);
                            return (
                                <CompletionButton
                                    key={locale.code}
                                    $isActive={activeLocale === locale.code}
                                    onClick={() => setActiveLocale(locale.code)}>
                                    <span>
                                        {percentage == 100 ? (
                                            <Icons
                                                name="check"
                                                size={15}
                                                ariaLabel={t('common:organization.completionStatus.complete')}
                                            />
                                        ) : (
                                            <Icons
                                                name="warningTriangle"
                                                size={15}
                                                ariaLabel={t('common:organization.completionStatus.incomplete')}
                                            />
                                        )}
                                    </span>
                                    <CompletionLabel>{locale.label}</CompletionLabel>
                                    <CompletionPercentage $isComplete={percentage === 100}>
                                        {percentage}%
                                    </CompletionPercentage>
                                </CompletionButton>
                            );
                        })}
                    </NavList>
                </Sidebar>

                {/* Main Content */}
                <MainContent>
                    {/* Document Description */}
                    <DocumentDescription>
                        <BodySmall>{t(`common:organization.${activeDocument}.editorDescription`)}</BodySmall>
                    </DocumentDescription>

                    {/* Editor */}
                    <EditorSection>
                        <EditorContainer>
                            {activeDocument === 'metadata' ? (
                                <MetadataForm>
                                    <StyledTextField
                                        id="title"
                                        label={t('common:organization.metadata.metatitle.label')}
                                        value={currentListing?.title || ''}
                                        onChange={(e) => handleMetadataFieldChange('title', e.target.value)}
                                        placeholder={t('common:organization.metadata.metatitle.placeholder')}
                                    />

                                    <StyledTextField
                                        id="shortDescription"
                                        label={t('common:organization.metadata.shortDescription.label')}
                                        value={currentListing?.shortDescription || ''}
                                        onChange={(e) => handleMetadataFieldChange('shortDescription', e.target.value)}
                                        placeholder={t('common:organization.metadata.shortDescription.placeholder')}
                                        multiline
                                        rows={3}
                                    />
                                    <BodyXSmall>{t('common:organization.metadata.iconUrl.description')}</BodyXSmall>

                                    <StyledTextField
                                        id="iconUrl"
                                        label={t('common:organization.metadata.iconUrl.label')}
                                        value={currentListing?.iconUrl || ''}
                                        onChange={(e) => handleMetadataFieldChange('iconUrl', e.target.value)}
                                        placeholder="Enter icon URL (must start with http:// or https://)"
                                        error={!isValidUrl(currentListing?.iconUrl)}
                                        helperText={
                                            !isValidUrl(currentListing?.iconUrl)
                                                ? t('common:organization.metadata.urlError')
                                                : ''
                                        }
                                    />

                                    <StyledTextField
                                        id="darkModeIconUrl"
                                        label={t('common:organization.metadata.darkModeIconUrl.label')}
                                        value={currentListing?.darkModeIconUrl || ''}
                                        onChange={(e) => handleMetadataFieldChange('darkModeIconUrl', e.target.value)}
                                        placeholder={t('common:organization.metadata.darkModeIconUrl.placeholder')}
                                        error={!isValidUrl(currentListing?.darkModeIconUrl)}
                                        helperText={
                                            !isValidUrl(currentListing?.darkModeIconUrl)
                                                ? t('common:organization.metadata.urlError')
                                                : ''
                                        }
                                    />

                                    <BodyXSmall>
                                        {t('common:organization.metadata.externalLink.description')}
                                    </BodyXSmall>

                                    <StyledTextField
                                        id="externalLink"
                                        label={t('common:organization.metadata.externalLink.label')}
                                        value={currentListing?.externalLink || ''}
                                        onChange={(e) => handleMetadataFieldChange('externalLink', e.target.value)}
                                        placeholder={t('common:organization.metadata.externalLink.placeholder')}
                                        error={
                                            !!(
                                                currentListing?.externalLink &&
                                                currentListing.externalLink.trim() !== '' &&
                                                !isValidUrl(currentListing?.externalLink, true)
                                            )
                                        }
                                        helperText={
                                            currentListing?.externalLink &&
                                            currentListing.externalLink.trim() !== '' &&
                                            !isValidUrl(currentListing?.externalLink, true)
                                                ? t('common:organization.metadata.urlError')
                                                : ''
                                        }
                                    />
                                </MetadataForm>
                            ) : (
                                <ResourceMarkdownEditor value={currentContent} onUpdate={handleContentChange} />
                            )}
                        </EditorContainer>
                    </EditorSection>
                </MainContent>
            </ContentWrapper>
        </>
    );
}
